import { Button, Spinner } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router";
import { useSession } from "@/services/auth";
import { SessionRecovery } from "@/components/session-recovery";

/** Keep sign-in on the app origin so the Auth0 transaction can be recovered.
 * A settled callback without a session must offer recovery instead of waiting
 * forever on a consumed or origin-mismatched code/state pair. */
export default function RootEntry() {
  const { t } = useTranslation("app");
  const { isLoading, isLoggedIn, sessionError } = useSession();
  const { search } = useLocation();

  const isAuthCallback = (() => {
    const p = new URLSearchParams(search);
    return p.has("state") && (p.has("code") || p.has("error"));
  })();

  if (!isLoading && sessionError) return <SessionRecovery />;

  if (!isLoading && isLoggedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isAuthCallback && !isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-default-50 p-6">
        <div role="alert" className="max-w-md space-y-4 text-center">
          <h1 className="text-xl font-semibold">{t("authCallback.title")}</h1>
          <p className="text-default-600">{t("authCallback.description")}</p>
          <Button
            color="primary"
            onPress={() => window.location.replace("/sign-in")}
          >
            {t("authCallback.retry")}
          </Button>
        </div>
      </div>
    );
  }

  if (!isLoading) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-default-50">
      <Spinner size="lg" color="primary" />
    </div>
  );
}
